export default function AboutSection() {
  const pillars = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Wealth Mastery Charter',
      description: 'Economic stewardship, enterprise development, and financial dominion through Founders, Entrepreneurs, and Investors Forums.',
      features: ['Founders Forum', 'Entrepreneurs Forum', 'Investors Forum']
    },
    {
      icon: 'ri-heart-line',
      title: 'Relationship Wholeness Charter',
      description: 'Emotional intelligence, covenantal commitment, and relational healing across all life stages.',
      features: ['Singles Forum', 'Married Forum', 'Post-Marriage Forum']
    },
    {
      icon: 'ri-parent-line',
      title: 'Generational Legacy Charter',
      description: 'Mentorship, cultural influence, heritage transfer, and identity building for lasting impact.',
      features: ['Fathers Forum', 'Youth Forum', 'Showbiz & Influence Forum']
    }
  ];

  const philosophies = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Purpose-Driven Wealth',
      description: 'Wealth is a tool for purpose, not self-indulgence',
      detail: 'We believe financial abundance should serve a greater mission, creating value for communities and future generations.',
      gradient: 'from-amber-400 to-orange-500'
    },
    {
      icon: 'ri-team-line',
      title: 'Relationship Capital',
      description: 'Healthy relationships are a form of capital',
      detail: 'Strong connections and emotional intelligence form the foundation of sustainable success and meaningful impact.',
      gradient: 'from-rose-400 to-pink-500'
    },
    {
      icon: 'ri-seedling-line',
      title: 'Intentional Legacy',
      description: 'Legacy is built intentionally, not inherited by default',
      detail: 'True legacy requires deliberate action, wisdom transfer, and conscious investment in future generations.',
      gradient: 'from-emerald-400 to-teal-500'
    }
  ];

  return (
    <section id="about" className="py-20" style={{ background: '#2d1b69' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
            The <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">CROWN</span> Movement
          </h2>
          <div className="text-2xl font-semibold mb-6 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
            Collaborative Restoration Of Wealth and Nations
          </div>
          <p className="text-xl max-w-4xl mx-auto leading-relaxed text-gray-100">
            A world where individuals, families, and communities walk in <strong className="text-amber-400">financial dominion</strong>, <strong className="text-amber-400">relational wholeness</strong>, and <strong className="text-amber-400">generational legacy</strong>. We unite entrepreneurs, founders, investors, families, and youth around a shared journey of abundance.
          </p>
        </div>

        {/* Enhanced Core Philosophy Section */}
        <div className="relative mb-16 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-20 right-0 w-24 h-24 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-0 left-1/3 w-28 h-28 bg-gradient-to-r from-amber-300 to-yellow-200 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative rounded-3xl p-12 border-2 border-amber-400/50 shadow-2xl bg-slate-900/60 backdrop-blur-sm">
            <div className="text-center mb-12">
              <h3 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
                Our Core Philosophy
              </h3>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {philosophies.map((philosophy, index) => (
                <div 
                  key={index} 
                  className="group relative rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-amber-400/30 bg-slate-800/80 backdrop-blur-sm"
                  style={{ 
                    animationDelay: `${index * 200}ms`,
                    animation: 'fadeInUp 0.8s ease-out forwards'
                  }}
                >
                  {/* Gradient Border Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${philosophy.gradient} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                  
                  {/* Icon Container */}
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-r ${philosophy.gradient} rounded-2xl flex items-center justify-center mx-auto shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${philosophy.icon} text-3xl text-white`}></i>
                    </div>
                    {/* Floating particles effect */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-amber-400 rounded-full opacity-60 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-rose-400 rounded-full opacity-40 animate-pulse" style={{ animationDelay: '1s' }}></div>
                  </div>

                  {/* Content */}
                  <div className="text-center">
                    <h4 className="text-xl font-bold mb-3 text-amber-400 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-amber-400 group-hover:to-yellow-300 transition-all duration-300">
                      {philosophy.title}
                    </h4>
                    <p className="font-semibold mb-4 leading-relaxed text-gray-100">
                      {philosophy.description}
                    </p>
                    <p className="text-sm leading-relaxed opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500 text-gray-300">
                      {philosophy.detail}
                    </p>
                  </div>

                  {/* Bottom accent line */}
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-gradient-to-r ${philosophy.gradient} rounded-full group-hover:w-3/4 transition-all duration-500`}></div>
                </div>
              ))}
            </div>

            {/* Central connecting element */}
            <div className="hidden lg:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 opacity-20">
              <div className="w-full h-full border-2 border-dashed border-amber-400 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                <i className="ri-crown-line text-2xl text-white"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Three Charter Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {pillars.map((pillar, index) => (
            <div key={index} className="rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border-2 border-amber-400/30 bg-slate-800/60 backdrop-blur-sm transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <i className={`${pillar.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-amber-400">{pillar.title}</h3>
              <p className="mb-6 leading-relaxed text-gray-100">{pillar.description}</p>
              <div className="space-y-2">
                {pillar.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center text-sm text-gray-200">
                    <i className="ri-check-line mr-2 text-amber-400"></i>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Mission Statement */}
        <div className="text-center rounded-2xl p-12 border-2 border-amber-400/50 bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm shadow-2xl">
          <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
            Our Mission
          </h3>
          <p className="text-xl leading-relaxed max-w-4xl mx-auto mb-8 text-gray-100">
            To empower and unite entrepreneurs, founders, investors, families, and youth around a shared journey of abundance — financially, relationally, and spiritually — anchored in values and practical tools.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Restoring</div>
              <div className="text-gray-200">Dominion</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Multiplying</div>
              <div className="text-gray-200">Legacy</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Building</div>
              <div className="text-gray-200">Abundance</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
